import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { CustomField } from '@assetaccountant/api/models/custom-field';
import {
  TypedFormBuilder,
  TypedFormControl,
  TypedValidatorFn,
} from '@assetaccountant/core/utils/typed-forms';
import { CustomFieldFormArray, CustomFieldFormGroup } from '../models/custom-field-form-model';

/** Helper service for managing a custom fields form. */
@Injectable({
  providedIn: 'root',
})
export class CustomFieldFormService {
  private labelValidators = [Validators.maxLength(64), labelPatternValidator, uniqueLabelValidator];
  private valueValidators = [Validators.maxLength(256)];

  constructor(private fb: TypedFormBuilder) {}

  createFormArray(): CustomFieldFormArray {
    const form = this.fb.array<CustomFieldFormGroup>([]);
    this.ensureHasBlankRow(form);
    return form;
  }

  setFormCustomFields(form: CustomFieldFormArray, customFields: CustomField[]) {
    form.clear();
    customFields?.forEach(customField => this.addFormCustomField(form, customField));
    this.ensureHasBlankRow(form);
  }

  addFormCustomField(form: CustomFieldFormArray, customField?: CustomField) {
    form.push(
      this.fb.group({
        label: [customField?.label ?? '', this.labelValidators],
        value: [customField?.value ?? '', this.valueValidators],
      }),
    );
  }

  removeFormCustomField(form: CustomFieldFormArray, index: number) {
    form.removeAt(index);
    form.markAsDirty();
    this.ensureHasBlankRow(form);
  }

  getFormCustomFields(form: CustomFieldFormArray): CustomField[] {
    // Get all non-blank rows
    return form.value.filter(customField => customField.label || customField.value);
  }

  private ensureHasBlankRow(form: CustomFieldFormArray) {
    const lastRow = form.controls.length > 0 ? form.controls[form.controls.length - 1] : null;
    // Add an blank row if there isn't one at end
    if (lastRow === null || lastRow.controls.label.value || lastRow.controls.value.value) {
      this.addFormCustomField(form);
    }
  }
}

// Disallow leading or trailing whitespace
const labelPatternValidator = Validators.pattern('^[\\S]+(.*[\\S]+)?$');

const uniqueLabelValidator: TypedValidatorFn<TypedFormControl<string>> = validatingControl => {
  const customFieldFormArray = validatingControl.parent?.parent as CustomFieldFormArray;
  if (!customFieldFormArray) {
    return null;
  }

  const label = validatingControl.value;

  // Ignore empty label
  if (!label) {
    return null;
  }

  for (const customFieldGroup of customFieldFormArray.controls) {
    const otherControl = customFieldGroup.controls.label;

    // Don't check against self
    if (otherControl === validatingControl) {
      continue;
    }
    const otherLabel = otherControl.value;

    // Case insensitive comparison
    if (label.localeCompare(otherLabel, undefined, { sensitivity: 'base' }) === 0) {
      return { duplicateLabel: { value: otherLabel } };
    }
  }
  return null;
};
